import type { Candidate, Choice, Screen } from "../types";

const CHOICES: { key: Choice; label: string }[] = [
  { key: "select", label: "Select" },
  { key: "skip", label: "Skip" },
  { key: "defer", label: "Defer" },
];

interface Props {
  candidate: Candidate;
  screen: Screen | undefined;
  choice: Choice | undefined;
  onChoose: (id: string, choice: Choice) => void;
  onBack: () => void;
  disabled: boolean;
}

export function CandidateDetail({
  candidate: c,
  screen,
  choice,
  onChoose,
  onBack,
  disabled,
}: Props) {
  return (
    <div className="app">
      <header className="header">
        <button className="back" onClick={onBack} aria-label="Back to round">
          ← Round
        </button>
        <h1>
          {c.ticker}
          {c.company && <span className="company"> · {c.company}</span>}
        </h1>
        {c.riskVerdict && (
          <div className="sub">
            <span className={`verdict ${c.riskVerdict}`}>
              {c.riskVerdict}
              {c.maxSizePct != null && ` ${c.maxSizePct}%`}
            </span>
          </div>
        )}
      </header>

      <main className="main detail">
        {c.business && (
          <section className="panel">
            <h2>What the business does</h2>
            <p>{c.business}</p>
          </section>
        )}

        <section className="panel">
          <h2>Why it surfaced</h2>
          <span className="screen-tag">{screen?.name ?? c.screen}</span>
          <p className="matched">{c.matched}</p>
          {c.coverage && <p className="muted">Coverage: {c.coverage}</p>}
        </section>

        {c.dataPoints.length > 0 && (
          <section className="panel">
            <h2>Data points</h2>
            <dl className="dp">
              {c.dataPoints.map((d) => (
                <div key={d.label} className="dp-row">
                  <dt>{d.label}</dt>
                  <dd>
                    <b>{d.value}</b>
                    {d.note && <span className="muted"> {d.note}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Sourced from ~/.claude/trading-council/screens.json - the same file
            candidate-screener reads. The UI must never paraphrase a screen in
            its own words, or it teaches a mental model the agent does not use. */}
        {screen && (
          <section className="panel screen-doc">
            <h2>About this screen</h2>
            <div className="chips">
              {screen.tier && <span className="chip">Tier {screen.tier}</span>}
              {screen.strength && <span className="chip">{screen.strength}</span>}
            </div>
            <h3>What it looks at</h3>
            <p>{screen.what}</p>
            <h3>Why it might carry signal</h3>
            <p>{screen.why}</p>
            <h3 className="warn">What it cannot tell you</h3>
            <p className="muted">{screen.limits}</p>
            {screen.source && <p className="src">Source: {screen.source}</p>}
          </section>
        )}

        {(c.thesis || c.bull || c.bear || c.riskNote) && (
          <section className="panel">
            <h2>Council</h2>
            <dl className="rows">
              {c.thesis && (
                <div className="row">
                  <dt>THESIS</dt>
                  <dd>{c.thesis}</dd>
                </div>
              )}
              {c.bull && (
                <div className="row bull">
                  <dt>BULL</dt>
                  <dd>{c.bull}</dd>
                </div>
              )}
              {c.bear && (
                <div className="row bear">
                  <dt>BEAR</dt>
                  <dd>{c.bear}</dd>
                </div>
              )}
              {c.riskNote && (
                <div className="row">
                  <dt>RISK</dt>
                  <dd>{c.riskNote}</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {c.evidence.length > 0 && (
          <section className="panel">
            <h2>Evidence</h2>
            <ul className="evidence">
              {c.evidence.map((e, i) => (
                <li key={i}>
                  <span>{e.claim}</span>
                  <span className="src">
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noreferrer noopener">
                        {e.source}
                      </a>
                    ) : (
                      e.source
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {c.notChecked && (
          <section className="panel unseen">
            <h2>What this does not tell you</h2>
            <p>{c.notChecked}</p>
          </section>
        )}
      </main>

      <div className="bar">
        <div className="bar-inner choices detail-choices" role="group"
             aria-label={`Decision for ${c.ticker}`}>
          {CHOICES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className="choice"
              data-c={key}
              aria-pressed={choice === key}
              disabled={disabled}
              onClick={() => {
                onChoose(c.id, key);
                onBack();
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
